import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Navbar, MobileNav } from "@/components/Navbar";
import { CreatorCard } from "@/components/CreatorCard";
import { DiscoveryMap } from "@/components/DiscoveryMap";
import { InviteCreatorButton } from "@/components/InviteCreatorButton";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { LocationSelect } from "@/components/LocationSelect";
import { Search, SlidersHorizontal, Loader2, MapPin, List, Map as MapIcon, Target, X } from "lucide-react";
import { toast } from "sonner";

const emptyFilters = {
  q: "", location: "all", niche: "all", platform: "all",
  min_followers: "", max_price: "", gifted: false, sort: "featured",
  origin: "none", distance: 20,
};

function FilterControls({ filters, setFilters, meta }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
        <Label className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Search near</Label>
        <LocationSelect testid="filter-origin" value={filters.origin} onChange={(v) => set("origin", v)}
          groups={meta.location_groups || []} leading={[{ value: "none", label: "Anywhere in the UK" }]}
          className="mt-1.5 h-11 bg-white" />
        {filters.origin !== "none" && (
          <div className="mt-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-emerald-700">Within</Label>
              <span data-testid="distance-value" className="font-mono text-sm font-bold text-emerald-700">{filters.distance} km</span>
            </div>
            <Slider data-testid="distance-slider" value={[filters.distance]} min={1} max={50} step={1}
              onValueChange={(v) => set("distance", v[0])} className="mt-2" />
          </div>
        )}
      </div>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Location</Label>
        <LocationSelect testid="filter-location" value={filters.location} onChange={(v) => set("location", v)}
          groups={meta.location_groups || []} leading={[{ value: "all", label: "All locations" }]} />
      </div>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Niche</Label>
        <Select value={filters.niche} onValueChange={(v) => set("niche", v)}>
          <SelectTrigger data-testid="filter-niche" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All niches</SelectItem>
            {meta.niches?.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Platform</Label>
        <Select value={filters.platform} onValueChange={(v) => set("platform", v)}>
          <SelectTrigger data-testid="filter-platform" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any platform</SelectItem>
            {(meta.platforms || []).map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Min followers</Label>
        <Input data-testid="filter-followers" type="number" value={filters.min_followers}
          onChange={(e) => set("min_followers", e.target.value)} className="mt-1.5 h-11" placeholder="e.g. 5000" />
      </div>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Max budget (£)</Label>
        <Input data-testid="filter-price" type="number" value={filters.max_price}
          onChange={(e) => set("max_price", e.target.value)} className="mt-1.5 h-11" placeholder="e.g. 100" />
      </div>
      <div className="flex items-center justify-between rounded-xl border border-zinc-200 p-3">
        <Label htmlFor="gifted" className="text-sm font-medium text-zinc-700">Open to gifted only</Label>
        <Switch id="gifted" data-testid="filter-gifted" checked={filters.gifted} onCheckedChange={(v) => set("gifted", v)} />
      </div>
      <Button variant="outline" data-testid="filter-reset" onClick={() => setFilters(emptyFilters)} className="w-full">
        Reset filters
      </Button>
    </div>
  );
}

export default function Discover() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteOppId = searchParams.get("invite");
  const [meta, setMeta] = useState({});
  const [filters, setFilters] = useState(emptyFilters);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [inviteOpp, setInviteOpp] = useState(null);
  const [invitedIds, setInvitedIds] = useState([]);
  const [myOpps, setMyOpps] = useState([]);
  const [matchOppId, setMatchOppId] = useState("");

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);

  useEffect(() => {
    if (user?.role === "business") {
      api.get("/opportunities/mine").then((r) => setMyOpps((r.data.opportunities || []).filter((o) => o.status === "published"))).catch(() => {});
    }
  }, [user]);
  useEffect(() => { if (inviteOppId) setMatchOppId(inviteOppId); }, [inviteOppId]);

  useEffect(() => {
    if (inviteOppId && user?.role === "business") {
      api.get(`/opportunities/${inviteOppId}`).then((r) => setInviteOpp(r.data)).catch(() => setInviteOpp(null));
    } else { setInviteOpp(null); }
  }, [inviteOppId, user]);

  const inviteCreator = async (creator) => {
    try {
      const r = await api.post(`/opportunities/${inviteOppId}/invite`, { creator_user_ids: [creator.user_id] });
      setInvitedIds((s) => [...s, creator.user_id]);
      if (r.data.invited > 0) toast.success(`${creator.display_name} invited 🎯`);
      else toast.info(`${creator.display_name} was already invited or has applied.`);
    } catch { toast.error("Could not send invitation"); }
  };

  useEffect(() => {
    if (user?.role === "business") {
      api.get("/business/profile/me").then((r) => {
        if (r.data.location) setFilters((f) => ({ ...f, origin: r.data.location, sort: "nearest" }));
      }).catch(() => {});
    }
  }, [user]);

  const fetchCreators = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.location !== "all") params.location = filters.location;
    if (filters.niche !== "all") params.niche = filters.niche;
    if (filters.platform !== "all") params.platform = filters.platform;
    if (filters.min_followers) params.min_followers = filters.min_followers;
    if (filters.max_price) params.max_price = filters.max_price;
    if (filters.gifted) params.gifted = true;
    if (filters.origin !== "none") { params.center = filters.origin; params.max_distance = filters.distance; }
    params.sort = matchOppId ? "best_match" : filters.sort;
    if (matchOppId) params.opportunity_id = matchOppId;
    try {
      const res = await api.get("/creators", { params });
      setCreators(res.data.creators);
    } finally {
      setLoading(false);
    }
  }, [filters, matchOppId]);

  useEffect(() => {
    const t = setTimeout(fetchCreators, 300);
    return () => clearTimeout(t);
  }, [fetchCreators]);

  const center = filters.origin !== "none" ? (meta.location_coords?.[filters.origin] || null) : null;

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-nav">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {inviteOpp && (
          <div data-testid="invite-mode-banner" className="mb-6 flex flex-col gap-3 rounded-2xl border border-sky-200 bg-sky-50 p-4 sm:flex-row sm:items-center">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-100 text-sky-600"><Target className="h-5 w-5" /></div>
            <div className="flex-1">
              <p className="font-display font-bold text-sky-900">Inviting creators to "{inviteOpp.title}"</p>
              <p className="text-sm text-sky-700">Tap Invite on any creator to send them a direct invitation.</p>
            </div>
            <Button data-testid="invite-mode-exit" variant="outline" onClick={() => navigate(`/business/opportunities/${inviteOppId}`)} className="rounded-full">
              <X className="mr-1 h-4 w-4" /> Done inviting
            </Button>
          </div>
        )}
        {user?.role === "business" && !inviteOpp && myOpps.length > 0 && (
          <div data-testid="find-for-opp" className="mb-6 flex flex-col gap-2 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 sm:flex-row sm:items-center sm:gap-3">
            <p className="text-sm font-semibold text-emerald-900">Find creators for…</p>
            <select data-testid="find-for-opp-select" value={matchOppId} onChange={(e) => setMatchOppId(e.target.value)} className="flex-1 rounded-full border border-emerald-200 bg-white px-3 py-2 text-sm text-zinc-700">
              <option value="">All creators (no specific opportunity)</option>
              {myOpps.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
            </select>
            {matchOppId && <span className="text-xs text-emerald-700">Showing best matches for this opportunity</span>}
          </div>
        )}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Discover creators</h1>
            <p className="mt-1 flex items-center gap-1 text-zinc-500">
              <MapPin className="h-4 w-4 text-emerald-600" /> Local creators near you
            </p>
          </div>
          <div className="hidden rounded-full border border-zinc-200 bg-white p-1 sm:flex">
            <button data-testid="view-list" onClick={() => setView("list")} className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium ${view === "list" ? "bg-zinc-900 text-white" : "text-zinc-500"}`}><List className="h-4 w-4" /> List</button>
            <button data-testid="view-map" onClick={() => setView("map")} className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium ${view === "map" ? "bg-zinc-900 text-white" : "text-zinc-500"}`}><MapIcon className="h-4 w-4" /> Map</button>
          </div>
        </div>

        <div className="mb-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <Input data-testid="discover-search" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Search by name, niche or keyword…" className="h-11 pl-9" />
          </div>
          <Select value={filters.sort} onValueChange={(v) => setFilters({ ...filters, sort: v })}>
            <SelectTrigger data-testid="discover-sort" className="h-11 sm:w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="featured">Featured first</SelectItem>
              <SelectItem value="nearest">Nearest</SelectItem>
              <SelectItem value="followers">Most followers</SelectItem>
              <SelectItem value="price_low">Lowest price</SelectItem>
            </SelectContent>
          </Select>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" data-testid="mobile-filter-btn" className="h-11 lg:hidden">
                <SlidersHorizontal className="mr-2 h-4 w-4" /> Filters
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto">
              <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
              <div className="mt-6"><FilterControls filters={filters} setFilters={setFilters} meta={meta} /></div>
            </SheetContent>
          </Sheet>
        </div>

        <div className="flex gap-8">
          <aside className="hidden w-64 shrink-0 lg:block">
            <div className="sticky top-24 rounded-2xl border border-zinc-200 bg-white p-5">
              <FilterControls filters={filters} setFilters={setFilters} meta={meta} />
            </div>
          </aside>

          <div className="flex-1">
            {view === "map" ? (
              <div className="space-y-4">
                <DiscoveryMap creators={creators} center={center} radius={filters.distance} height={520} />
                <p className="text-sm text-zinc-500">{creators.length} creator{creators.length !== 1 ? "s" : ""} shown on map</p>
              </div>
            ) : loading ? (
              <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
            ) : creators.length === 0 ? (
              <div data-testid="discover-empty" className="flex h-64 flex-col items-center justify-center rounded-2xl border border-dashed border-zinc-300 text-center">
                <p className="font-display text-lg font-semibold text-zinc-700">No creators match your filters</p>
                <p className="mt-1 text-sm text-zinc-500">Try widening your distance or resetting filters.</p>
                {user?.role === "creator" && (
                  <div className="mt-4 flex flex-col items-center gap-1.5">
                    <p className="text-sm text-zinc-500">Can't find who you're looking for?</p>
                    <InviteCreatorButton label="Invite them to Nearfluence" />
                  </div>
                )}
              </div>
            ) : (
              <>
                {center && (
                  <div className="mb-4"><DiscoveryMap creators={creators} center={center} radius={filters.distance} height={260} /></div>
                )}
                <p className="mb-4 text-sm text-zinc-500">{creators.length} creator{creators.length !== 1 ? "s" : ""} found{filters.origin !== "none" ? ` within ${filters.distance} km of ${filters.origin}` : ""}</p>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                  {creators.map((c, i) => <CreatorCard key={c.user_id} creator={c} index={i} inviteMode={!!inviteOpp} invited={invitedIds.includes(c.user_id)} onInvite={inviteCreator} />)}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <MobileNav />
    </div>
  );
}
